/**
 * @description Labor law compliance states configuration
 *
 * States with specific labor law requirements that warrant a warning badge
 * on location cards and trigger compliance notices in Global Settings.
 *
 * Key features:
 * - dailyOT: Hours after which daily overtime applies (federal is weekly only)
 * - mealBreakAfterHours: Hours worked before meal break is required
 * - mealBreakMinutes: Duration of required meal break
 * - restBreakEveryHours: Hours between required rest breaks
 * - restBreakMinutes: Duration of required rest breaks
 *
 * @notes
 * - V1 is hardcoded. Future versions may store in database for admin customization.
 * - States not in this list follow federal labor law (no daily OT, minimal break requirements).
 * - Compliance is derived from location.state, not stored as a flag.
 *
 * @see .claude/tasks/time-clock-employee-portal.md - Phase 10
 */

export const COMPLIANCE_STATES = {
  'CA': {
    name: 'California',
    dailyOT: 8,
    mealBreakAfterHours: 5,
    mealBreakMinutes: 30,
    restBreakEveryHours: 4,
    restBreakMinutes: 10,
    message: 'California has specific daily overtime and meal/rest break requirements'
  },
  'WA': {
    name: 'Washington',
    mealBreakAfterHours: 5,
    mealBreakMinutes: 30,
    restBreakEveryHours: 4,
    restBreakMinutes: 10,
    message: 'Washington has specific meal and rest break requirements'
  },
  'OR': {
    name: 'Oregon',
    mealBreakAfterHours: 6,
    mealBreakMinutes: 30,
    restBreakEveryHours: 4,
    restBreakMinutes: 10,
    message: 'Oregon has specific meal and rest break requirements'
  },
  'NY': {
    name: 'New York',
    mealBreakAfterHours: 6,
    mealBreakMinutes: 30,
    message: 'New York has specific meal break requirements'
  },
  'MA': {
    name: 'Massachusetts',
    mealBreakAfterHours: 6,
    mealBreakMinutes: 30,
    message: 'Massachusetts has specific meal break requirements'
  },
  'CO': {
    name: 'Colorado',
    dailyOT: 12,
    mealBreakAfterHours: 5,
    mealBreakMinutes: 30,
    restBreakEveryHours: 4,
    restBreakMinutes: 10,
    message: 'Colorado has specific overtime and meal/rest break requirements'
  }
};

/**
 * Check if a state has specific compliance requirements
 * @param {string} stateCode - 2-letter US state code
 * @returns {boolean}
 */
export const hasComplianceRequirements = (stateCode) => {
  return stateCode && Object.prototype.hasOwnProperty.call(COMPLIANCE_STATES, stateCode.toUpperCase());
};

/**
 * Get compliance info for a state
 * @param {string} stateCode - 2-letter US state code
 * @returns {Object|null} Compliance requirements or null if standard federal rules apply
 */
export const getComplianceInfo = (stateCode) => {
  if (!stateCode) return null;
  return COMPLIANCE_STATES[stateCode.toUpperCase()] || null;
};

/**
 * Get the compliance message for a state
 * @param {string} stateCode - 2-letter US state code
 * @returns {string|null}
 */
export const getComplianceMessage = (stateCode) => {
  const info = getComplianceInfo(stateCode);
  return info?.message || null;
};

export default COMPLIANCE_STATES;
